import React from 'react'
import { Music, Share2, Book } from 'lucide-react'

interface SpotifyPreviewContentProps {
  data: {
    title?: string
    description?: string
    target_url?: string
    bookTitle?: string
    author?: string
    isbn?: string
    coverUrl?: string
    showBookCover?: boolean
    playerTheme?: 'light' | 'dark'
    fonts?: {
      heading?: string
      body?: string
    }
    textSizes?: {
      bookTitle?: string
      description?: string
    }
    companyPosition?: 'top' | 'bottom'
  }
  customColors?: {
    primaryColor: string
    secondaryColor: string
    textColor: string
    wrapperColor?: string
    wrapperOpacity?: number
  }
  colorMode?: 'gradient' | 'structured'
  brandingType?: 'beyondpages' | 'custom' | 'none'
  userBranding?: {
    companyName?: string
    logoUrl?: string
    primaryColor?: string
    secondaryColor?: string
    textColor?: string
    displayType?: 'logo_only' | 'logo_and_name' | 'name_only'
  }
  isPreview?: boolean
}

export const SpotifyPreviewContent: React.FC<SpotifyPreviewContentProps> = ({ 
  data, 
  customColors,
  colorMode = 'gradient',
  brandingType = 'beyondpages',
  userBranding,
  isPreview = true
}) => {
  // Extract Spotify ID from URL
  const getSpotifyId = (url: string) => {
    if (!url) return null
    const match = url.match(/(?:spotify\.com\/|spotify:)(?:playlist|track|album)(?:\/|:)([a-zA-Z0-9]+)/)
    return match ? match[1] : null
  }

  const getSpotifyType = (url: string) => {
    if (!url) return 'playlist'
    if (url.includes('track')) return 'track'
    if (url.includes('album')) return 'album'
    return 'playlist'
  }

  const spotifyId = getSpotifyId(data.target_url || '')
  const spotifyType = getSpotifyType(data.target_url || '')
  const playerTheme = data.playerTheme || 'dark'

  // Load fonts
  const fontLinks = []
  if (data.fonts?.heading && data.fonts.heading !== 'Inter') {
    fontLinks.push(
      <link
        key={`heading-${data.fonts.heading}`}
        href={`https://fonts.googleapis.com/css2?family=${data.fonts.heading.replace(' ', '+')}:wght@400;700&display=swap`}
        rel="stylesheet"
      />
    )
  }
  if (data.fonts?.body && data.fonts.body !== 'Inter' && data.fonts.body !== data.fonts?.heading) {
    fontLinks.push(
      <link
        key={`body-${data.fonts.body}`}
        href={`https://fonts.googleapis.com/css2?family=${data.fonts.body.replace(' ', '+')}:wght@400;700&display=swap`}
        rel="stylesheet"
      />
    )
  }

  // Apply padding for preview mode only
  const paddingClass = isPreview ? 'pt-14' : 'pt-0'

  // Render custom branding only (BeyondPages footer is handled globally at page end)
  const renderCustomBranding = () => {
    if (brandingType !== 'custom' || !userBranding) return null
    return (
      <div className="flex items-center justify-center gap-2 py-3">
        {userBranding.displayType !== 'name_only' && userBranding.logoUrl && (
          <img src={userBranding.logoUrl} alt={userBranding.companyName} className="h-6 object-contain" />
        )}
        {userBranding.displayType !== 'logo_only' && userBranding.companyName && (
          <span className={data.textSizes?.branding || 'text-lg'} style={{ fontFamily: data.fonts?.heading || 'Inter' }}>
            {userBranding.companyName}
          </span>
        )}
      </div>
    )
  }

  if (colorMode === 'structured') {
    // STRUCTURED MODE - 65/35 split like autorenprofil
    return (
      <div className={`min-h-screen ${paddingClass} flex flex-col`}>
        {fontLinks}
        
        {/* Header Section (65%) */}
        <div 
          className="flex-1" 
          style={{ 
            backgroundColor: customColors?.primaryColor || '#3B82F6',
            color: customColors?.textColor || '#FFFFFF',
            flex: '0 0 65%'
          }}
        >
          <div className="h-full flex flex-col">
            {/* Top Branding (custom only) */}
            {brandingType === 'custom' && data.companyPosition === 'top' && (
              <div className="px-6">{renderCustomBranding()}</div>
            )}
            
            {/* Main Content */}
            <div className="flex-1 px-6 py-8 flex flex-col justify-center">
              {/* Book Info with Cover */}
              {(data.coverUrl || data.bookTitle || data.author) && (
                <div className="mb-8">
                  <div className="flex items-start gap-4">
                    {data.coverUrl && (
                      <img
                        src={data.coverUrl}
                        alt={data.bookTitle || 'Buchcover'}
                        className="w-24 h-32 object-cover rounded shadow-lg flex-shrink-0"
                      />
                    )}
                    <div className="flex-1">
                      {data.bookTitle && (
                        <h1 
                          className={`font-bold mb-2 ${data.textSizes?.bookTitle || 'text-2xl'}`}
                          style={{ fontFamily: data.fonts?.heading || 'Inter' }}
                        >
                          {data.bookTitle}
                        </h1>
                      )}
                      {data.author && (
                        <p 
                          className="opacity-90 text-lg mb-2"
                          style={{ fontFamily: data.fonts?.body || 'Inter' }}
                        >
                          von {data.author}
                        </p>
                      )}
                      {data.isbn && (
                        <p 
                          className="text-sm opacity-75"
                          style={{ fontFamily: data.fonts?.body || 'Inter' }}
                        >
                          ISBN: {data.isbn}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* Optional description */}
              {data.description && (
                <div className="text-center">
                  <p 
                    className={`opacity-90 ${data.textSizes?.description || 'text-base'}`}
                    style={{ fontFamily: data.fonts?.body || 'Inter' }}
                  >
                    {data.description}
                  </p>
                </div>
              )}
            </div>
            
            {/* Bottom Branding for top section (custom only) */}
            {brandingType === 'custom' && data.companyPosition === 'bottom' && (
              <div className="px-6">{renderCustomBranding()}</div>
            )}
          </div>
        </div>

        {/* Body Section (35%) - Spotify Player */}
        <div 
          className="flex-1"
          style={{ 
            backgroundColor: customColors?.secondaryColor || '#DBEAFE',
            color: '#1F2937',
            flex: '0 0 35%'
          }}
        >
          <div className="h-full flex flex-col justify-center items-center px-6 py-6">
            {/* Spotify Player */}
            {spotifyId ? (
              <div className="w-full mb-4">
                <iframe
                  src={`https://open.spotify.com/embed/${spotifyType}/${spotifyId}?theme=${playerTheme === 'light' ? '0' : '1'}`}
                  width="100%"
                  height="152"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  className="rounded-lg shadow-lg"
                  style={{ minHeight: '152px' }}
                />
              </div>
            ) : (
              <div className="w-full bg-gray-900 rounded-lg h-[152px] flex items-center justify-center mb-4">
                <div className="text-center">
                  <Music className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-white text-sm">Spotify Player</p>
                </div>
              </div>
            )}
            
            {/* Action Button */}
            <button 
              className="px-8 py-3 rounded-full font-medium flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow"
              style={{ 
                backgroundColor: customColors?.primaryColor || '#3B82F6',
                color: customColors?.textColor || '#FFFFFF'
              }}
            >
              <Music className="w-5 h-5" />
              Auf Spotify hören
            </button>
          </div>
        </div>
        {/* Global footer branding at page end for BeyondPages */}
        {brandingType === 'beyondpages' && (
          <div className="text-center py-4">
            <p className="text-sm opacity-60">Powered by BeyondPages</p>
          </div>
        )}
      </div>
    )
  } else {
    // GRADIENT MODE - Original design
    const bgGradient = `linear-gradient(135deg, ${customColors?.primaryColor || '#4F46E5'}, ${customColors?.secondaryColor || '#7C3AED'})`
    
    return (
      <div 
        className={`min-h-screen ${paddingClass} relative`}
        style={{ background: bgGradient }}
      >
        {fontLinks}
        
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-20 left-20 w-40 h-40 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 container mx-auto px-4 py-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 shadow-2xl">
            {/* Book Cover Section */}
            {data.showBookCover && data.coverUrl && (
              <div className="flex items-start gap-4 mb-6">
                <img
                  src={data.coverUrl}
                  alt={data.bookTitle || 'Buchcover'}
                  className="w-20 h-28 object-cover rounded-lg shadow-xl"
                />
                <div className="flex-1" style={{ color: customColors?.textColor || '#FFFFFF' }}>
                  {data.bookTitle && (
                    <h2 
                      className="text-lg font-bold mb-1"
                      style={{ fontFamily: data.fonts?.heading || 'Inter' }}
                    >
                      {data.bookTitle}
                    </h2>
                  )}
                  {data.author && (
                    <p className="opacity-75 text-xs mt-1">von {data.author}</p>
                  )}
                </div>
              </div>
            )}

            {/* Title */}
            <h1 
              className={`font-bold mb-4 text-center ${data.textSizes?.bookTitle || 'text-2xl'}`}
              style={{ 
                color: customColors?.textColor || '#FFFFFF',
                fontFamily: data.fonts?.heading || 'Inter'
              }}
            >
              {data.title || 'Spotify Playlist'}
            </h1>

            {/* Spotify Player */}
            <div className="bg-black/40 backdrop-blur-sm rounded-xl p-2 mb-4">
              {spotifyId ? (
                <iframe
                  src={`https://open.spotify.com/embed/${spotifyType}/${spotifyId}?theme=${playerTheme === 'light' ? '0' : '1'}`}
                  width="100%"
                  height="352"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  className="rounded-lg"
                />
              ) : (
                <div className="bg-gray-900 rounded-lg h-[352px] flex items-center justify-center">
                  <div className="text-center">
                    <Music className="w-16 h-16 text-green-500 mx-auto mb-2" />
                    <p className="text-white">Spotify Player</p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {data.description && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-4">
                <p 
                  className={`${data.textSizes?.description || 'text-base'} leading-relaxed`}
                  style={{ 
                    color: customColors?.textColor || '#FFFFFF',
                    fontFamily: data.fonts?.body || 'Inter'
                  }}
                >
                  {data.description}
                </p>
              </div>
            )}

            {/* Action Button */}
            <button 
              className="w-full px-4 py-3 rounded-full font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow"
              style={{ 
                backgroundColor: '#10B981',
                color: '#FFFFFF'
              }}
            >
              <Music className="w-5 h-5" />
              Auf Spotify anhören
            </button>

            {/* Branding inside card removed for BeyondPages; custom branding stays controlled above */}
          </div>
        </div>
        {/* Global footer branding at page end */}
        {brandingType === 'beyondpages' && (
          <div className="text-center py-4">
            <p className="text-white/80 text-sm">Powered by BeyondPages</p>
          </div>
        )}
        {brandingType === 'custom' && (
          <div className="py-2 flex items-center justify-center">
            {renderCustomBranding()}
          </div>
        )}
      </div>
    )
  }
  
}
